// @flow

import React from "react";
import { localize } from "redux-i18n";
import { Field, reduxForm } from "redux-form";
import { Dropdown } from "@components/form";
import {
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Button,
  Col,
  Form,
  FormGroup,
  Label,
} from "@components";
import { connect } from "react-redux";
import type { State } from "@store/reducers";
import { selectAddEditShiftPatternValues } from "@modules/hierarchy/selectors";
import type { AssignedShiftPattern } from "../../reducer/hierarchy/types";
import type { FormProps } from "redux-form";

type PropsFromState = $Exact<{
  formData: ?Object,
}>;

type OwnProps = $Exact<{
  schema: ?Object,
  assignedShiftPattern: ?AssignedShiftPattern,
  toggleModal: (
    $Exact<{
      visible: boolean,
      assignedShiftPattern?: ?AssignedShiftPattern,
    }>,
  ) => () => void,
  onSubmit: (formData: Object) => void,
}>;
type Props = $Exact<{
  ...PropsFromLocalize,
  ...PropsFromState,
  ...$Exact<FormProps>, // https://stackoverflow.com/a/44771268/220839
  ...OwnProps,
}>;

export class AddEditShiftPatternModal extends React.Component<Props> {
  componentDidMount() {
    const modalData = this.props.assignedShiftPattern || {
      isCascading: true,
      shiftPattern: {
        id: (this.dropdownShiftPatternOptions[0] || {}).value,
      },
    };

    this.props.initialize &&
      this.props.initialize({
        isCascading: modalData.isCascading.toString(),
        shiftPattern: modalData.shiftPattern.id,
      });
  }

  get modalContent() {
    return !this.props.assignedShiftPattern
      ? {
          modalTitle: this.props.t("ADD_SHIFT_PATTERN"),
          shiftPatternTitle: this.props.t("SELECT_SHIFT_PATTERN"),
          submitButtonIcon: "add_box",
          submitButtonName: this.props.t("ADD"),
        }
      : {
          modalTitle: this.props.t("EDIT_SHIFT_PATTERN"),
          shiftPatternTitle: this.props.t("SET_SHIFT_PATTERN"),
          submitButtonIcon: "save",
          submitButtonName: this.props.t("SAVE"),
        };
  }

  get dropdownCascadingOptions() {
    return [
      {
        label: this.props.t("THIS_DEPARTMENT_AND_ITS_CHILDREN"),
        value: "true",
      },
      {
        label: this.props.t("THIS_DEPARTMENT_ONLY"),
        value: "false",
      },
    ];
  }

  handleSubmit = () => {
    this.props.onSubmit({
      id: this.props.assignedShiftPattern && this.props.assignedShiftPattern.id,
      isCascading: this.props.formData.isCascading,
      shiftPattern: {
        id: this.props.formData.shiftPattern,
      },
    });
  };

  get dropdownShiftPatternOptions(): Array<{ value: number, label: string }> {
    const options =
      (
        ((((this.props.schema || {}).properties || {}).shiftPattern || {}).properties || {}).id ||
        {}
      ).oneOf || [];

    return options.map(item => ({
      value: item.const,
      label: item.title,
    }));
  }

  render() {
    return (
      <Modal>
        <ModalHeader tag="h3" toggle={this.props.toggleModal({ visible: false })}>
          {this.modalContent.modalTitle}
        </ModalHeader>
        <ModalBody>
          <Col sm={{ size: 8, offset: 2 }}>
            <div className="w-100 d-flex justify-content-center">
              {this.dropdownShiftPatternOptions.length ? (
                <Form className="d-flex w-100 flex-column px-0">
                  <FormGroup className="mb-3 mr-2 p-0 w-100">
                    <Label for="shiftPattern">{this.modalContent.shiftPatternTitle}</Label>
                    <Field
                      id="shiftPattern"
                      name="shiftPattern"
                      component={Dropdown}
                      options={this.dropdownShiftPatternOptions}
                    />
                  </FormGroup>
                  <FormGroup className="mb-0 mr-2 p-0 w-100">
                    <Label for="isCascading">{this.props.t("DEPARTMENT_SCOPE")}</Label>
                    <Field
                      id="isCascading"
                      name="isCascading"
                      component={Dropdown}
                      options={this.dropdownCascadingOptions}
                    />
                  </FormGroup>
                </Form>
              ) : (
                <div className="text-center d-flex flex-column mt-3">
                  {this.props.t("NO_SHIFT_PATTERNS_FOUND")}
                </div>
              )}
            </div>
          </Col>
        </ModalBody>
        <ModalFooter className="d-flex justify-content-end">
          <Button color="secondary" onClick={this.props.toggleModal({ visible: false })}>
            {this.props.t("CANCEL")}
          </Button>
          <Button
            materialIcon={this.modalContent.submitButtonIcon}
            disabled={!this.dropdownShiftPatternOptions.length}
            onClick={this.handleSubmit}
          >
            {this.modalContent.submitButtonName}
          </Button>
        </ModalFooter>
      </Modal>
    );
  }
}

const mapStateToProps = (state: State): PropsFromState => ({
  formData: selectAddEditShiftPatternValues(state),
});

export default reduxForm({
  form: "add-edit-shift-pattern",
})(localize()(connect(mapStateToProps, null)(AddEditShiftPatternModal)));
